import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiFehler } from "../api/client";

/** Natives <dialog>: Fokusfalle, Esc und Hintergrund bringt der Browser mit. */
export function Dialog({ offen, zu, titel, children, fuss }: {
  offen: boolean; zu: () => void; titel: string; children: ReactNode; fuss: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) d.showModal();
    if (!offen && d.open) d.close();
  }, [offen]);
  return (
    <dialog ref={ref} className="dialog" onClose={zu}>
      <h2>{titel}</h2>
      <div style={{ marginTop: 14 }}>{children}</div>
      <div className="dialog-fuss">{fuss}</div>
    </dialog>
  );
}

export function Feld({ titel, children, breit }: { titel: string; children: ReactNode; breit?: boolean }) {
  return (
    <label className={`feld${breit ? " breit" : ""}`}>
      <span>{titel}</span>
      {children}
    </label>
  );
}

export function Meldung({ fehler }: { fehler: unknown }) {
  if (!fehler) return null;
  const text = fehler instanceof ApiFehler || fehler instanceof Error ? fehler.message : String(fehler);
  return <div className="meldung">{text}</div>;
}

/**
 * Ein Textfeld, das beim Verlassen speichert (E16) — kein Speichern-Knopf, den man in
 * der Besprechung vergisst. Der Wert von außen gewinnt nur, solange nicht getippt wird;
 * sonst spränge der Cursor beim Nachladen mitten im Satz.
 */
export function Blur({ wert, speichern, mehrzeilig, ...rest }: {
  wert: string;
  speichern: (v: string) => void;
  mehrzeilig?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  type?: string;
  "aria-label"?: string;
}) {
  const [v, setV] = useState(wert);
  const fokus = useRef(false);
  // Der Serverstand, auf dem das Feld zuletzt aufsetzte. Nachgezogen wird NUR, wenn sich
  // `wert` aendert, und nur, solange niemand davon abweicht. Vorher hing der Abgleich auch
  // an `fokus`: beim Verlassen sprang das Feld sofort auf den alten Text zurueck, und
  // scheiterte das Speichern (Funkloch, 409, 422), war das Getippte weg.
  const basis = useRef(wert);
  useEffect(() => {
    setV((jetzt) => (!fokus.current && (jetzt === basis.current || jetzt === wert) ? wert : jetzt));
    basis.current = wert;
  }, [wert]);
  const props = {
    ...rest,
    className: `${rest.className ?? "eingabe"}${v !== wert ? " ungespeichert" : ""}`,
    value: v,
    onFocus: () => { fokus.current = true; },
    onBlur: () => {
      fokus.current = false;
      if (v !== wert) speichern(v);
    },
  };
  if (mehrzeilig) {
    return <textarea {...props} rows={Math.min(8, Math.max(2, v.split("\n").length + 1))}
                     onChange={(e) => setV(e.target.value)} />;
  }
  return <input {...props} onChange={(e) => setV(e.target.value)} />;
}
